<template>
  <div v-if="(user && config) || (isDemo && config)" class="relative">
    <div v-if="shellMode === 'onboarding'" class="onboarding-shell-canvas min-h-dvh overflow-hidden pt-16">
      <OnboardingShellHeader />
      <div class="mx-auto min-h-[calc(100dvh-4rem)] max-w-5xl px-4 pt-12 pb-10 sm:px-6 sm:pt-14 lg:px-8">
        <router-view />
      </div>
    </div>

    <div
      v-else
      data-shell="frame"
      class="flex h-dvh flex-col bg-overlay dark:bg-canvas"
      :class="{ 'pb-14': isDemo, 'cursor-col-resize select-none [&_iframe]:pointer-events-none': rightyDock.resizing }">
      <TopBar :righty-available="rightyAvailable" />
      <div class="flex min-h-0 flex-1">
        <aside
          data-shell="sidebar"
          class="hidden shrink-0 flex-col overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex"
          :class="sidebarRail ? 'w-14' : 'w-54'">
          <Sidebar
            :rail="sidebarRail"
            :can-toggle="shellMode === 'app' && !(rightyAvailable && rightyStore.isDrawerOpen && !isRightyMobile)" />
        </aside>
        <section
          data-shell="page-card"
          class="relative isolate z-10 flex min-w-0 flex-1 flex-col overflow-hidden bg-white transition-[margin-right] duration-200 ease-out motion-reduce:transition-none lg:mb-2 lg:rounded-[10px] lg:shadow-sm lg:ring-1 lg:ring-[rgb(38_35_58/0.1)] dark:bg-panel dark:lg:ring-[rgb(223_218_217/0.2)]"
          :class="{ 'lg:mr-2': !rightyAvailable || !rightyStore.isDrawerOpen }">
          <div ref="pageScroll" data-shell="page-scroll" class="min-h-0 flex-1 overflow-y-auto">
            <div
              :class="shellMode === 'editor' ? '' : 'mx-auto max-w-7xl space-y-12 px-4 py-10 sm:px-6 lg:px-8'"
              :style="shellMode === 'editor' ? undefined : { paddingBottom: pagePaddingBottom }">
              <router-view />
            </div>
          </div>
          <BarStack>
            <PublishBar />
          </BarStack>
        </section>
        <aside
          v-if="rightyAvailable"
          data-shell="righty-sidebar"
          aria-label="Righty sidebar"
          :inert="!rightyStore.isDrawerOpen"
          :aria-hidden="!rightyStore.isDrawerOpen"
          class="fixed inset-0 z-1200 shrink-0 md:relative md:inset-auto md:z-0 md:w-[var(--righty-sidebar-width)] lg:mb-2"
          :class="[
            rightyDock.resizing
              ? 'transition-none'
              : 'transition-[width,opacity,transform,visibility] duration-200 ease-out motion-reduce:transition-none',
            rightyStore.isDrawerOpen
              ? 'visible translate-x-0 opacity-100'
              : 'invisible translate-x-full opacity-0 md:translate-x-0 md:opacity-100'
          ]"
          :style="{ '--righty-dock-width': `${rightyDock.width}px` }"
          @transitionend="handleRightyTransitionEnd">
          <div
            v-if="rightyStore.isDrawerOpen"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize Righty"
            :aria-valuenow="rightyDock.width"
            :aria-valuemin="rightyDock.min"
            :aria-valuemax="rightyDock.max"
            tabindex="0"
            data-shell="righty-resize-handle"
            class="group absolute inset-y-0 left-0 z-10 hidden w-2 cursor-col-resize touch-none outline-none md:block"
            @pointerdown="rightyDock.onPointerdown"
            @pointermove="rightyDock.onPointermove"
            @pointerup="rightyDock.onPointerend"
            @pointercancel="rightyDock.onPointerend"
            @lostpointercapture="rightyDock.onPointerend"
            @keydown="rightyDock.onKeydown"
            @keyup="rightyDock.onKeyup">
            <span
              aria-hidden="true"
              class="pointer-events-none absolute inset-y-3 left-0.5 w-0.5 rounded-full bg-primary/50 transition-opacity duration-150 motion-reduce:transition-none"
              :class="
                rightyDock.resizing
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
              " />
          </div>
          <div
            data-shell="righty-surface"
            class="relative h-full bg-panel before:absolute before:inset-y-0 before:-left-4 before:w-4 before:bg-panel lg:mr-2 lg:rounded-r-[10px] dark:bg-overlay dark:before:bg-overlay">
            <div class="h-full overflow-hidden rounded-[inherit]">
              <div
                data-shell="righty-content"
                class="h-full w-full transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none md:w-[var(--righty-dock-width)]"
                :class="rightyStore.isDrawerOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'"
                @click="closeRightyAfterLink">
                <RightyWindow v-if="rightyLoaded" />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>

    <ElementPickerDialog
      v-if="elementPickerSession"
      :key="elementPickerSession.id"
      :options="elementPickerSession.options"
      @complete="finishElementPicker" />

    <ConfigVersionDrawer
      v-if="shellMode !== 'onboarding'"
      :visible="versionHistoryOpen"
      @update:visible="versionHistoryOpen = $event" />

    <ConnectionLostBanner />

    <!-- Lazy-loaded Command Palette (Cmd+K) -->
    <CommandPalette v-if="shellMode !== 'onboarding' && commandPaletteOpen" v-model:open="commandPaletteOpen" />

    <OutcomeChooserDialog
      v-if="shellMode !== 'onboarding'"
      :visible="outcomeChooserVisible"
      :source="outcomeChooserSource"
      @update:visible="outcomeChooserVisible = $event" />

    <BillingDialog v-if="!isDemo" :key="String(route.params.teamPid)" :team-pid="String(route.params.teamPid)" />

    <!-- Demo Mode -->
    <template v-if="isDemo">
      <DemoWelcomeOverlay />
      <DemoCtaBar />
    </template>

    <!-- Account Overdue Modal -->
    <Upgrade v-if="showTrialExpiredModal" :key="String(route.params.teamPid)" :closeable="false" :is-blocking="true" />
  </div>
  <div v-else-if="isError" class="flex h-screen items-center justify-center">
    <div class="text-center">
      <h1 class="text-2xl font-bold">There's an error loading your team's configuration.</h1>
      <p class="text-gray-500">Please contact support at support@rightmessage.com</p>
      <div class="mt-4">
        <TeamDropdown class="w-3xl" />
      </div>
    </div>
  </div>

  <!-- Loading state - matches inline loader for seamless transition -->
  <div v-else class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900">
    <svg
      class="h-12 w-12 animate-spin text-slate-700 dark:text-slate-200"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
    <p class="mt-4 text-sm text-slate-500">Loading RightMessage...</p>
  </div>
</template>

<script setup lang="ts">
import { useOutcomeChooser } from '@dashboard/modules/activation/composables/useOutcomeChooser'
import { useBillingPortalErrorNotification } from '@dashboard/composables/useBillingPortalErrorNotification'
import { usePaymentImportNotices } from '@dashboard/composables/usePaymentImportNotices'
import { useRightyConfigMutationSync } from '@dashboard/modules/righty/composables/useRightyConfigMutationSync'
import { useRightyKeyboardShortcuts } from '@dashboard/modules/righty/composables/useRightyKeyboardShortcuts'
import { useRightyDockWidth } from '@dashboard/modules/righty/composables/useRightyDockWidth'
import { useRightyStore } from '@dashboard/modules/righty/stores/rightyStore'
import { useOnboardingStore } from '@dashboard/modules/onboarding/stores/onboardingStore'
import { useSubscriptionStore } from '@dashboard/stores/subscriptionStore'
import DemoCtaBar from '@dashboard/components/common/DemoCtaBar.vue'
import DemoWelcomeOverlay from '@dashboard/components/common/DemoWelcomeOverlay.vue'

import ConfigVersionDrawer from '@dashboard/components/common/ConfigVersionDrawer.vue'
import ElementPickerDialog from '@dashboard/components/common/ElementPickerDialog.vue'
import { elementPickerSession, finishElementPicker } from '@dashboard/composables/useElementPicker'
import BarStack from '@dashboard/components/common/BarStack.vue'
import ConnectionLostBanner from '@dashboard/components/common/ConnectionLostBanner.vue'
import PublishBar from '@dashboard/components/common/PublishBar.vue'
import TeamDropdown from '@dashboard/components/common/TeamDropdown.vue'
import OnboardingShellHeader from '@dashboard/components/layouts/OnboardingShellHeader.vue'
import Sidebar from '@dashboard/components/layouts/Sidebar.vue'
import TopBar from '@dashboard/components/layouts/TopBar.vue'
import { useBottomStack } from '@dashboard/composables/useBottomStack'
import { useCommandPalette } from '@dashboard/composables/useCommandPalette'
import { usePageScrollRestoration } from '@dashboard/composables/usePageScrollRestoration'
import { usePostHog } from '@dashboard/composables/usePosthog'
import { useRightMessageBridge } from '@dashboard/composables/useRightMessageBridge'
import { useSidebarCollapse } from '@dashboard/composables/useSidebarCollapse'
import { useVersionHistoryDrawer } from '@dashboard/composables/useVersionHistoryDrawer'

import { useBillingDialogStore } from '@dashboard/stores/billingDialogStore'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { usePermissionStore } from '@dashboard/stores/permissionStore'
import { useTeamStore } from '@dashboard/stores/teamStore'
import { useQueryClient } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { useMediaQuery } from '@vueuse/core'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  provide,
  reactive,
  ref,
  useTemplateRef,
  watch
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Lazy-load heavy components to reduce initial bundle size
const RightyWindow = defineAsyncComponent(() => import('@dashboard/modules/righty/components/RightyWindow.vue'))
const CommandPalette = defineAsyncComponent(() => import('@dashboard/components/common/CommandPalette.vue'))
const OutcomeChooserDialog = defineAsyncComponent(
  () => import('@dashboard/modules/activation/components/OutcomeChooserDialog.vue')
)

onBeforeUnmount(() => finishElementPicker(null))

const { visible: outcomeChooserVisible, source: outcomeChooserSource } = useOutcomeChooser()
const Upgrade = defineAsyncComponent(() => import('@dashboard/modules/settings/pages/Upgrade.vue'))
const BillingDialog = defineAsyncComponent(() => import('@dashboard/components/billing/BillingDialog.vue'))

const route = useRoute()

// Initialize Righty keyboard shortcuts (Cmd+/ to toggle, Escape to close).
useRightyKeyboardShortcuts()
const rightyStore = useRightyStore()
const rightyLoaded = ref(rightyStore.isDrawerOpen)
watch(
  () => rightyStore.isDrawerOpen,
  (open) => {
    if (open) {
      rightyLoaded.value = true
    }
  }
)

const queryClient = useQueryClient()
// Registered here, not in the lazily mounted RightyWindow, so a turn that settles
// before the drawer has ever opened (a recovered or recommendation turn) still
// refreshes what Righty changed.
useRightyConfigMutationSync({
  rightyStore,
  refetchConfig: () => {
    const configStore = useConfigStore()
    configStore.requestRightyRefetch()
    void queryClient.invalidateQueries({ queryKey: ['splitTests', configStore.currentTeamPid] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard-recommendations', configStore.currentTeamPid] })
    void queryClient.invalidateQueries({ queryKey: ['recommendations', configStore.currentTeamPid] })
  }
})

useBillingPortalErrorNotification()
usePaymentImportNotices()

// Bridge user/plan state to window.rm_app_user for RightMessage tag.js flows
useRightMessageBridge()

const authStore = useAuthStore()
const { user, isAdmin } = storeToRefs(authStore)
const configStore = useConfigStore()
const { config, currentTeamPid, isError } = storeToRefs(configStore)
const { canAccess } = usePermissionStore()
provide('canAccess', canAccess)

useTeamStore()

const { identifyUser, setTeamGroup } = usePostHog()

const { isOpen: commandPaletteOpen } = useCommandPalette()
const { isOpen: versionHistoryOpen } = useVersionHistoryDrawer()

// Keep the end of the page scrollable past the bars stacked over it.
const { barStackBox } = useBottomStack()
const pagePaddingBottom = computed(() => {
  const height = barStackBox.value?.height ?? 0
  return height > 0 ? `${height + 40}px` : undefined
})
usePageScrollRestoration(useTemplateRef<HTMLElement>('pageScroll'))

// Check if we're in demo mode
const isDemo = computed(() => route.params.teamPid === 'demo')
const onboardingStore = useOnboardingStore()
const { hasMeaningfulFirstWinProgress } = storeToRefs(onboardingStore)
const isOnboardingShell = computed(() => route.meta?.shell === 'onboarding' && !hasMeaningfulFirstWinProgress.value)
const shellMode = computed<'onboarding' | 'editor' | 'app'>(() => {
  if (isOnboardingShell.value) {
    return 'onboarding'
  }
  return route.meta?.fullScreen ? 'editor' : 'app'
})
const { userCollapsed } = useSidebarCollapse()
const sidebarRail = computed(
  () =>
    shellMode.value === 'editor' ||
    (rightyAvailable.value && rightyStore.isDrawerOpen && !isRightyMobile.value) ||
    userCollapsed.value
)

// Provide isDemo context for components that need to know if we're in demo mode
provide('isDemo', isDemo)

const subscriptionStore = useSubscriptionStore()
const { isTrialExpired, requiresReactivation, requiresPaymentRecovery, needsCurrentTeamBillingSetup, trialActivation } =
  storeToRefs(subscriptionStore)

const showTrialExpiredModal = computed(() => {
  if (isDemo.value || isAdmin.value || trialActivation.value) {
    return false
  }
  return isTrialExpired.value || requiresReactivation.value || Boolean(requiresPaymentRecovery.value)
})

const rightyAvailable = computed(
  () => shellMode.value !== 'onboarding' && !isDemo.value && !showTrialExpiredModal.value
)
const isRightyMobile = useMediaQuery('(max-width: 767px)')
/** Full-screen Righty covers the page, so a link it opens in this tab closes it to show where the link went. */
function closeRightyAfterLink(event: MouseEvent) {
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null
  const inApp = link instanceof HTMLAnchorElement && link.origin === window.location.origin
  const newTab =
    link?.getAttribute('target') === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  if (isRightyMobile.value && inApp && !newTab) {
    rightyStore.closeDrawer()
  }
}
const hasRightyInset = useMediaQuery('(min-width: 1024px)')
const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

async function announceRightyOpened() {
  await nextTick()
  if (rightyStore.isDrawerOpen) {
    window.dispatchEvent(new Event('righty:sidebar-opened'))
  }
}

// Live content width; a finished drag (or an unanimated key step) refits like an open.
const rightyDock = reactive(
  useRightyDockWidth({ animated: computed(() => !reducedMotion.value), onResized: announceRightyOpened })
)

function handleRightyTransitionEnd(event: TransitionEvent) {
  if (event.target === event.currentTarget && event.propertyName === 'width' && rightyStore.isDrawerOpen) {
    window.dispatchEvent(new Event('righty:sidebar-opened'))
  }
}

watch(
  () => rightyStore.isDrawerOpen,
  (open) => {
    if (!open) {
      rightyDock.endResize()
    } else if (reducedMotion.value && !isRightyMobile.value) {
      void announceRightyOpened()
    }
  }
)
watch(
  [() => rightyStore.isDrawerOpen, rightyAvailable, isRightyMobile, hasRightyInset, () => rightyDock.width],
  ([open, available, mobile, inset, width]) => {
    document.documentElement.style.setProperty(
      '--righty-sidebar-width',
      open && available && !mobile ? `${width + (inset ? 8 : 0)}px` : '0px'
    )
  },
  { immediate: true, flush: 'sync' }
)
onBeforeUnmount(() => document.documentElement.style.setProperty('--righty-sidebar-width', '0px'))

const showProjectBillingSetup = computed(() => {
  if (isDemo.value || showTrialExpiredModal.value) {
    return false
  }
  return route.query.billingSetup === '1' && needsCurrentTeamBillingSetup.value
})

const router = useRouter()
const billingDialog = useBillingDialogStore()

watch(
  () => route.params.teamPid,
  () => billingDialog.close()
)
watch(
  [showProjectBillingSetup, () => route.params.teamPid],
  ([show]) => {
    if (show) {
      billingDialog.open({ mode: 'project-setup', source: 'project-setup', blocking: true })
    } else if (billingDialog.visible && billingDialog.mode === 'project-setup') {
      billingDialog.close()
    }
  },
  { immediate: true }
)
watch(
  () => billingDialog.visible,
  async (open) => {
    if (open || route.query.billingSetup !== '1') {
      return
    }
    const { billingSetup, ...query } = route.query
    await router.replace({ query })
  }
)

const { currentTeam } = storeToRefs(configStore)

// Identify user to PostHog when authenticated
watch(
  user,
  (newUser) => {
    if (newUser && !isDemo.value) {
      identifyUser({
        id: newUser.id,
        email: newUser.email,
        name: newUser.name ?? undefined,
        created_at: newUser.created_at ?? undefined,
        trial_ends_at: newUser.trial_ends_at,
        free_account: newUser.free_account,
        on_trial: newUser.on_trial,
        current_billing_plan: newUser.current_billing_plan ?? undefined,
        stripe_status: newUser.stripe_status ?? undefined,
        active_subscription: newUser.active_subscription
          ? { stripe_status: newUser.active_subscription.stripe_status ?? undefined }
          : null,
        is_agency: newUser.is_agency,
        first_promoter_ref_id: newUser.first_promoter_ref_id,
        owned_teams: newUser.owned_teams
      })
    }
  },
  { immediate: true }
)

// Set team group context when team changes
watch(
  currentTeam,
  (team) => {
    if (team && !isDemo.value) {
      setTeamGroup({
        pid: team.pid,
        name: team.name,
        current_billing_plan: team.current_billing_plan
      })
    }
  },
  { immediate: true }
)

watch(
  () => route.params.teamPid,
  () => {
    const teamPid = route.params.teamPid as string
    // Handle demo mode - keep as string 'demo', otherwise parse as integer
    currentTeamPid.value = teamPid === 'demo' ? 'demo' : parseInt(teamPid, 10)
  },
  { immediate: true }
)
</script>

<style scoped>
.onboarding-shell-canvas {
  --dot-bg: #f8fafc;
  --dot-color: #d4dbe4;
  --dot-size: 1px;
  --dot-space: 16px;
  background-color: var(--dot-bg);
  background-image: radial-gradient(circle, var(--dot-color) var(--dot-size), transparent var(--dot-size));
  background-size: var(--dot-space) var(--dot-space);
  background-position: 0 0;
}

:deep(.dark) .onboarding-shell-canvas,
.dark .onboarding-shell-canvas {
  --dot-bg: #1e1e2e;
  --dot-color: rgba(110, 106, 134, 0.3);
}
</style>
